import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Lazy-load pages for smaller initial bundle
const Dashboard = lazy(() => import("@/pages/Dashboard").then(m => ({ default: m.Dashboard })));
const CanvasEngine = lazy(() => import("@/pages/CanvasEngine").then(m => ({ default: m.CanvasEngine })));
const UnifiedSnapshot = lazy(() => import("@/pages/UnifiedSnapshot").then(m => ({ default: m.UnifiedSnapshot })));
const CanvasBuilder = lazy(() => import("@/pages/CanvasBuilder").then(m => ({ default: m.CanvasBuilder })));
// TemplateLibrary is deprecated — /templates now redirects to /builder
const CanvasCompare = lazy(() => import("@/pages/CanvasCompare").then(m => ({ default: m.CanvasCompare })));
const AdminUsers = lazy(() => import("@/pages/AdminUsers").then(m => ({ default: m.AdminUsers })));
const AdminSettings = lazy(() => import("@/pages/AdminSettings").then(m => ({ default: m.AdminSettings })));
const Login = lazy(() => import("@/pages/Login").then(m => ({ default: m.Login })));

// Redirect stubs for deprecated routes
const SpatialRedirect = () => { const { canvasId } = useParams(); return <Navigate to={`/canvas/${canvasId}/snapshot`} replace />; };
const AnalyticsRedirect = () => { const { canvasId } = useParams(); return <Navigate to={`/canvas/${canvasId}/snapshot`} replace />; };

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Protected routes — creators must be logged in */}
                <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/builder" element={<ProtectedRoute><CanvasBuilder /></ProtectedRoute>} />
                <Route path="/templates" element={<Navigate to="/builder" replace />} />
                <Route path="/compare" element={<ProtectedRoute><CanvasCompare /></ProtectedRoute>} />

                {/* Admin-only routes */}
                <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
                <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />

                {/* Public routes — clients answer via shared link, no login needed */}
                <Route path="/canvas" element={<CanvasEngine />} />
                <Route path="/canvas/:canvasId" element={<CanvasEngine />} />
                <Route path="/canvas/:canvasId/snapshot" element={<UnifiedSnapshot />} />

                {/* Deprecated routes — redirect to Unified Snapshot */}
                <Route path="/canvas/:canvasId/spatial" element={<SpatialRedirect />} />
                <Route path="/canvas/:canvasId/analytics" element={<AnalyticsRedirect />} />

                {/* Auth routes — always accessible */}
                <Route path="/login" element={<Login />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster />
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
